import type { ReactNode } from 'react'
import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { teamInitials } from 'components/TeamCard/team-card-avatar'
import { DARK_TEXT_STEEL } from 'constants/dark-tokens'
import { buildOrgSurface, hexToRgba, resolveOrgAccent } from 'utils/banner-gradient'
import BannerActionButton from './banner-action-button'
import { resolveTitleFontSize } from './title-font-size'

// One banner for three states in the design:
//   organization selected        → title is the organization name
//   team selected                → title is the team name, label keeps the org
//   team selected, coach viewing → same, plus a "Manage team" action
// They differ only in `title` and whether `onManageTeam` is passed, so splitting
// them into separate components would duplicate the whole surface for nothing.

const BANNER_HEIGHT = 102
const BANNER_PADDING = 16
const LOGO_SIZE = BANNER_HEIGHT - BANNER_PADDING * 2

interface IOrgTeamBannerProps {
  /** Organization name — always the label, even when the title is a team. */
  orgName: string
  /** Sports for the label. Empty/absent drops the separator rather than leaving a dangling '·'. */
  sports?: string[]
  /** Organization name, or the team name once a team is selected. */
  title: string
  logoUrl?: string
  /** Organization.primaryColor. Invalid or missing falls back inside buildOrgSurface. */
  primaryColor?: string
  /** Stored size token; unknown values resolve to the default. */
  titleFontSize?: string
  /**
   * Extra caps segments appended to the label after the sports, each preceded by
   * its own separator dot — e.g. `8 TEAMS`, `123 MEMBERS` on the Organization
   * page. Empty strings are dropped so a still-loading count leaves no stray dot.
   */
  metaSegments?: string[]
  /**
   * Renders the "Manage team" action when provided. Deliberately no permission
   * check here — the team detail page opens read-only and gates its own edit
   * toggle, so re-deriving "can manage" would create a second source of truth.
   */
  onManageTeam?: () => void
  /** Replaces the Manage team action with a caller-supplied one (Switch Org). */
  action?: ReactNode
}

const OrgTeamBanner = ({
  orgName,
  sports,
  title,
  logoUrl,
  primaryColor,
  titleFontSize,
  metaSegments,
  onManageTeam,
  action
}: IOrgTeamBannerProps) => {
  const surface = buildOrgSurface(primaryColor)
  const accent = resolveOrgAccent(primaryColor)
  const titlePx = resolveTitleFontSize(titleFontSize)
  // Casing is left to `text-transform` so the underlying value stays readable.
  const sportLabel = sports?.filter(Boolean).join(' · ')
  // Sports and the counts share one type style, so they render through the same
  // list; sports stay first because that is the design's reading order.
  const labelSegments = [sportLabel, ...(metaSegments ?? [])].filter(
    (segment): segment is string => !!segment && segment.trim().length > 0
  )

  return (
    <HStack
      {...surface}
      h={{ base: 'auto', md: `${BANNER_HEIGHT}px` }}
      minH={{ base: '84px', md: undefined }}
      p={`${BANNER_PADDING}px`}
      spacing="8px"
      align="center"
      // No width: the caller stretches this to the content column.
      w="100%"
      overflow="hidden"
    >
      {logoUrl ? (
        <Box
          as="img"
          src={logoUrl}
          alt={orgName}
          w={{ base: '56px', md: `${LOGO_SIZE}px` }}
          h={{ base: '56px', md: `${LOGO_SIZE}px` }}
          borderRadius="10px"
          objectFit="cover"
          flexShrink={0}
        />
      ) : (
        <Box
          w={{ base: '56px', md: `${LOGO_SIZE}px` }}
          h={{ base: '56px', md: `${LOGO_SIZE}px` }}
          borderRadius="10px"
          border={`1px solid ${hexToRgba(accent, 0.4)}`}
          bg="rgba(255,255,255,0.06)"
          flexShrink={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="white"
          fontSize="22px"
          fontWeight={700}
        >
          {teamInitials(orgName)}
        </Box>
      )}

      {/* 14px label + 8px gap + 48px title = 70px, exactly the height left by the
          banner's 16px padding. Changing any of the three breaks that fit. */}
      <VStack align="stretch" spacing="8px" flex={1} minW={0} pl="8px">
        {/* Two distinct type styles, not one string: the organization is the
            branded half (condensed, accent-coloured, widely tracked) and the sport
            is neutral supporting text. Rendering them as one Text was the wrong
            shape for the design. */}
        <HStack spacing="8px" align="center" minW={0}>
          <Text
            fontFamily="display"
            fontWeight={700}
            fontSize="9px"
            lineHeight="14px"
            letterSpacing="2.5px"
            textTransform="uppercase"
            color={accent}
            m={0}
            noOfLines={1}
            flexShrink={0}
          >
            {orgName}
          </Text>

          {labelSegments.map(segment => (
            // Each count is its own segment so a narrow screen can drop them
            // wholesale — four segments each clipped to "1…" tells the reader nothing.
            <HStack key={segment} spacing="8px" align="center" minW={0} display={{ base: 'none', md: 'flex' }}>
              <Text fontSize="10px" lineHeight="12px" color={DARK_TEXT_STEEL} m={0} flexShrink={0}>
                ·
              </Text>
              <Text
                fontFamily="body"
                fontWeight={600}
                fontSize="10px"
                lineHeight="12px"
                letterSpacing="0.02em"
                textTransform="uppercase"
                color={DARK_TEXT_STEEL}
                m={0}
                noOfLines={1}
              >
                {segment}
              </Text>
            </HStack>
          ))}
        </HStack>

        <HStack spacing="12px" align="center" minW={0}>
          <Text
            fontFamily="display"
            fontWeight={700}
            // The design size from `md` up. On a phone it steps down instead: at
            // 48px a name like "Manufacturing" is cut to four letters, and a
            // readable smaller title beats a truncated large one.
            fontSize={{ base: `${Math.round(titlePx * 0.6)}px`, md: `${titlePx}px` }}
            lineHeight={{ base: '32px', md: '48px' }}
            letterSpacing="-0.5px"
            textTransform="uppercase"
            color="white"
            m={0}
            noOfLines={1}
          >
            {title}
          </Text>

          {action}
          {!action && onManageTeam && (
            <BannerActionButton accent={accent} label="Manage team >" onClick={onManageTeam} />
          )}
        </HStack>
      </VStack>
    </HStack>
  )
}

export default OrgTeamBanner
